import React from 'react';
import Cookies from 'js-cookie';
import {compose} from 'recompose';

import {ClassNameProps} from '@yandex-data-ui/cloud-components';
import withLocale, {WithLocaleProps} from 'hoc/withLocale';
import withRouter, {WithRouterProps} from 'hoc/withRouter';
import {NotifyTooltip} from 'components/NotifyTooltip/NotifyTooltip';
import {I18nKFunction, initI18n, loadKeyset} from 'i18n';
import {getAbsoluteLocaleUrl} from '../../../../../common/i18n/utils';
import {GEO_DATA_COOKIE_NAME} from '../../../../../common/constants';
import metrika from 'counters/metrika';
import {base64DecodeUnicode} from 'utils';
import {Locale} from 'contexts/LocaleContext';

const keysetName = 'region-tooltip';
const noRegionCookieName = 'no-region-popup';

type GeoData = {
    locale: Locale;
    regionName?: string;
    countryName?: string;
    timezone?: string;
};

type RegionTooltipProps = WithRouterProps & WithLocaleProps & ClassNameProps;

interface RegionTooltipState {
    visible: boolean;
    geoData: GeoData | null;
    i18nK: I18nKFunction | null;
}

class RegionTooltip extends React.Component<RegionTooltipProps, RegionTooltipState> {
    state: RegionTooltipState = {
        visible: false,
        geoData: null,
        i18nK: null,
    };

    componentDidMount() {
        const {locale} = this.props;

        if (this.noRender) {
            return;
        }

        try {
            const geoDataBase64 = Cookies.get(GEO_DATA_COOKIE_NAME);
            if (!geoDataBase64) {
                return;
            }

            const geoData: GeoData = JSON.parse(base64DecodeUnicode(geoDataBase64));
            this.setState({geoData});
            if (geoData.locale.code !== locale.code) {
                this.fetchKeyset(geoData.locale.code);
            }
        } catch (ex) {
            return;
        }
    }

    componentDidUpdate() {
        const {visible} = this.state;
        if (visible) {
            metrika.reachGoal('main', metrika.Goal.REGION_POPUP_SHOW);
        }
    }

    render() {
        const {
            router: {hostname, as},
            className,
        } = this.props;
        const {visible, geoData, i18nK} = this.state;

        if (!visible || !geoData?.countryName || !i18nK || this.noRender) {
            return null;
        }

        const country = geoData.countryName;
        const {currency} = geoData.locale;

        return (
            <NotifyTooltip
                className={className}
                title={i18nK('title', {country, currency})}
                content={i18nK('content', {country, currency})}
                actionButton={{
                    text: i18nK('button-yes'),
                    onClick: () => {
                        window.location.href = getAbsoluteLocaleUrl(geoData.locale, hostname, as);
                        metrika.reachGoal('main', metrika.Goal.REGION_POPUP_YES);
                    },
                }}
                cancelButton={{
                    text: i18nK('button-no'),
                    onClick: () => {
                        metrika.reachGoal('main', metrika.Goal.REGION_POPUP_NO);
                        this.setCookies();
                        this.setState({visible: false});
                    },
                }}
                onCloseClick={() => {
                    metrika.reachGoal('main', metrika.Goal.REGION_POPUP_CLOSE);
                    this.setCookies();
                }}
            />
        );
    }

    private get noRender() {
        return Boolean(Cookies.get(noRegionCookieName));
    }

    private setCookies = () => {
        const {
            router: {hostname},
        } = this.props;
        Cookies.set(noRegionCookieName, String(true), {expires: 365, domain: hostname});
    };

    private fetchKeyset = async (localeCode: string) => {
        const keyset = await loadKeyset(localeCode, keysetName);
        const i18nK = initI18n({[keysetName]: keyset}).bind(null, keysetName);
        this.setState({
            i18nK,
            visible: Boolean(i18nK),
        });
    };
}

export default compose<RegionTooltipProps, ClassNameProps>(withLocale, withRouter)(RegionTooltip);
